/** Advisor invitation → client setup → Client ID. Isolated from buyer/seller checklist state. */

import { getUserByEmail } from "../db";
import { setCredential } from "../db/credentials";
import { resolveProIdentity } from "./proIdentity";
import { createDeskClient } from "./deskClients";
import { logClientActivity } from "./clientActivity";
import { portalPathForClient } from "./clientAccess";

const KEY = "dh_client_invites";

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function writeAll(rows) {
  localStorage.setItem(KEY, JSON.stringify(rows));
}

function invitePath(token) {
  return `/invite/${encodeURIComponent(token)}`;
}

export function inviteHref(token) {
  const path = invitePath(token);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export function getInvite(token) {
  const needle = String(token || "").trim();
  if (!needle) return null;
  return readAll().find((row) => row.token === needle) || null;
}

export function listDeskInvites(session) {
  if (!session?.userId) return [];
  return readAll()
    .filter((row) => {
      if (session.role === "broker") {
        return row.brokerId === session.userId || row.invitedBy === session.userId;
      }
      return row.assignedAgentId === session.userId || row.invitedBy === session.userId;
    })
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

export function createClientInvite({
  session,
  kind,
  name,
  email,
  phone = "",
  address = "",
  notes = "",
  assignedAgentId = "",
}) {
  const role = kind === "seller" ? "seller" : kind === "buyer" ? "buyer" : null;
  if (!role) throw new Error("Choose buyer or seller.");
  const trimmedName = String(name || "").trim();
  const trimmedEmail = String(email || "").trim().toLowerCase();
  if (!trimmedName || !trimmedEmail) throw new Error("Name and email are needed.");
  if (!session?.userId) throw new Error("Sign in to your Desk first.");
  if (getUserByEmail(trimmedEmail)) {
    throw new Error("That email is already on a desk.");
  }
  const pending = readAll().find((row) => row.email === trimmedEmail && row.status === "pending");
  if (pending) return pending;

  const identity = resolveProIdentity(session) || {};
  const token = `inv_${crypto.randomUUID().replace(/-/g, "").slice(0, 20)}`;
  const row = {
    token,
    status: "pending",
    kind: role,
    name: trimmedName,
    email: trimmedEmail,
    phone: String(phone || "").trim(),
    address: String(address || "").trim(),
    notes: String(notes || "").trim(),
    assignedAgentId: assignedAgentId || session.userId,
    invitedBy: session.userId,
    inviterRole: session.role,
    brokerId: session.role === "broker" ? session.userId : session.brokerageId || null,
    advisorName: identity.name || session.name || "Your advisor",
    advisorEmail: identity.email || session.email || "",
    officeName: identity.officeName || session.office_name || "",
    clientId: null,
    clientCode: "",
    created_at: new Date().toISOString(),
    accepted_at: null,
  };
  writeAll([row, ...readAll()].slice(0, 200));
  return row;
}

export function inviteEmailDraft(invite) {
  const kind = invite?.kind === "seller" ? "seller" : "buyer";
  const href = inviteHref(invite.token);
  const subject = `Your REChecklist ${kind} account setup`;
  const body = [
    `Hello ${invite?.name || ""},`,
    "",
    `${invite.advisorName || "Your advisor"} has invited you to REChecklist.`,
    `This note is only for your ${kind} account setup — not the professional desk.`,
    "",
    `Open this link to complete your setup:`,
    href,
    "",
    "After you finish the short form, you will receive a Client ID. That ID is how you and your advisor stay connected.",
    "",
    "Your information stays with the office. If this note reached you in error, please delete it and contact the office.",
    invite.advisorEmail ? `Office: ${invite.advisorEmail}` : "",
  ]
    .filter((line) => line !== "")
    .join("\n");
  const mailto = `mailto:${encodeURIComponent(invite.email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return { subject, body, href, mailto };
}

export function markInviteSent(invite) {
  const rows = readAll().map((row) =>
    row.token === invite.token
      ? { ...row, sent_at: new Date().toISOString(), last_action: "invitation_sent" }
      : row
  );
  writeAll(rows);
  return rows.find((row) => row.token === invite.token);
}

export async function acceptClientInvite(token, { name, phone, password }) {
  const invite = getInvite(token);
  if (!invite) throw new Error("This invitation was not found.");
  if (invite.status === "accepted") throw new Error("This invitation has already been used.");
  if (invite.status !== "pending") throw new Error("This invitation is no longer open.");
  const trimmedName = String(name || invite.name || "").trim();
  const trimmedPassword = String(password || "").trim();
  if (!trimmedName) throw new Error("Your name is needed.");
  if (trimmedPassword.length < 6) throw new Error("Choose a password of at least six characters.");

  const session = {
    userId: invite.inviterRole === "broker" ? invite.brokerId || invite.invitedBy : invite.assignedAgentId,
    role: invite.inviterRole === "broker" ? "broker" : "agent",
    name: invite.advisorName,
    email: invite.advisorEmail,
    brokerageId: invite.brokerId,
    office_name: invite.officeName,
  };

  const created = await createDeskClient({
    session,
    kind: invite.kind,
    name: trimmedName,
    email: invite.email,
    phone: phone || invite.phone,
    address: invite.address,
    notes: invite.notes,
    assignedAgentId: invite.assignedAgentId,
    password: trimmedPassword,
  });

  setCredential(created.client.id, trimmedPassword);
  const rows = readAll().map((row) =>
    row.token === token
      ? {
          ...row,
          status: "accepted",
          accepted_at: new Date().toISOString(),
          name: trimmedName,
          phone: phone || invite.phone,
          clientId: created.client.id,
          clientCode: created.client.client_code,
        }
      : row
  );
  writeAll(rows);
  logClientActivity(created.client.id, "setup_completed", {
    actorId: created.client.id,
    clientCode: created.client.client_code,
    advisorId: invite.assignedAgentId,
    kind: invite.kind,
  });
  return {
    client: created.client,
    invite: rows.find((row) => row.token === token),
    portalPath: portalPathForClient(created.client),
  };
}
